import { get, refreshSession } from '@/api/client';
import { connectSocket } from '@/api/socket';
import type { PublicUser } from '@/api/types';
// Корень приложения: загрузка сессии, роутинг (экраны грузятся лениво), cookie-баннер, PWA-обновления.
import { AndroidUpdatePrompt, BundleUpdateHint } from '@/components/AndroidApp';
import { NewbieShieldPrompt } from '@/components/NewbieShieldPrompt';
import { VerifyWatcher } from '@/components/VerifyWatcher';
import { applyDirection, isLang, setLanguage } from '@/i18n';
import { CookieBanner } from '@/layout/CookieBanner';
import { GameLayout } from '@/layout/GameLayout';
import { initPlatform, platform } from '@/lib/platform';
import { registerServiceWorker } from '@/lib/pwa';
import { startMusic, unlockAudio } from '@/lib/sound';
import { linkPlatformAccount } from '@/pages/auth/session';
import { useGame } from '@/store/game';
import { Loading, Toaster } from '@/ui';
import { Suspense, lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useLocation, useNavigate } from 'react-router-dom';

const Landing = lazy(() => import('@/pages/auth/Landing'));
const Login = lazy(() => import('@/pages/auth/Login'));
const PlatformStart = lazy(() => import('@/pages/auth/PlatformStart'));
const OAuthReturn = lazy(() => import('@/pages/auth/OAuthReturn'));
const Register = lazy(() => import('@/pages/auth/Register'));
const VerifyEmail = lazy(() => import('@/pages/auth/VerifyEmail'));
const Forgot = lazy(() => import('@/pages/auth/Forgot'));
const ResetPassword = lazy(() => import('@/pages/auth/ResetPassword'));
const DeleteConfirm = lazy(() => import('@/pages/auth/DeleteConfirm'));
const CredentialsConfirm = lazy(() => import('@/pages/auth/CredentialsConfirm'));
const Legal = lazy(() => import('@/pages/legal/Legal'));
const City = lazy(() => import('@/pages/game/City'));
const MapPage = lazy(() => import('@/pages/game/MapPage'));
const Army = lazy(() => import('@/pages/game/Army'));
const Heroes = lazy(() => import('@/pages/game/Heroes'));
const Alliance = lazy(() => import('@/pages/game/Alliance'));
const Politics = lazy(() => import('@/pages/game/Politics'));
const Shop = lazy(() => import('@/pages/game/Shop'));
const TestPay = lazy(() => import('@/pages/game/TestPay'));
const Quests = lazy(() => import('@/pages/game/Quests'));
const Reports = lazy(() => import('@/pages/game/Reports'));
const Marches = lazy(() => import('@/pages/game/Marches'));
const Mail = lazy(() => import('@/pages/game/Mail'));
const Sea = lazy(() => import('@/pages/game/Sea'));
const Rankings = lazy(() => import('@/pages/game/Rankings'));
const BattleMath = lazy(() => import('@/pages/game/BattleMath'));
const BattlePass = lazy(() => import('@/pages/game/BattlePass'));
const Boosts = lazy(() => import('@/pages/game/Boosts'));
const Events = lazy(() => import('@/pages/game/Events'));
const Profile = lazy(() => import('@/pages/game/Profile'));
const AdminApp = lazy(() => import('@/admin/AdminApp'));

function RequireAuth({ children }: { children: JSX.Element }) {
  const user = useGame((s) => s.user);
  const location = useLocation();
  if (!user)
    return <Navigate to={`/login?next=${encodeURIComponent(location.pathname + location.search)}`} replace />;
  return children;
}

export function App() {
  const [ready, setReady] = useState(false);
  const setUser = useGame((s) => s.setUser);
  const setFlags = useGame((s) => s.setFlags);
  const navigate = useNavigate();

  useEffect(() => {
    registerServiceWorker();
    const unlock = () => {
      unlockAudio();
      startMusic();
    };
    window.addEventListener('pointerdown', unlock, { once: true });
    void (async () => {
      // Внутри платформы (Telegram, VK, Яндекс): SDK платформы до входа
      if (platform) await initPlatform();
      const signedIn = !location.pathname.startsWith('/admin') && (await refreshSession());
      if (signedIn) {
        try {
          const me = await get<PublicUser>('/auth/me');
          setUser(me);
          if (isLang(me.locale)) setLanguage(me.locale);
          document.documentElement.dataset.theme = me.theme;
          connectSocket();
          void linkPlatformAccount();
          setFlags((await get<{ flags: string[] }>('/flags')).flags);
        } catch {
          setUser(null);
        }
      }
      applyDirection(document.documentElement.lang);
      // Платформа и нет сессии — вход/регистрация через платформу без почты
      if (platform && !signedIn && !location.pathname.startsWith('/platform'))
        navigate('/platform', { replace: true });
      else if (platform && signedIn && location.pathname === '/') navigate('/game', { replace: true });
      setReady(true);
    })();
  }, [setUser, setFlags, navigate]);

  if (!ready) return <Loading />;
  return (
    <>
      <Suspense fallback={<Loading />}>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/platform" element={<PlatformStart />} />
          <Route path="/oauth" element={<OAuthReturn />} />
          <Route path="/register" element={<Register />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route path="/forgot" element={<Forgot />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/account/delete-confirm" element={<DeleteConfirm />} />
          <Route path="/account/credentials-confirm" element={<CredentialsConfirm />} />
          <Route path="/legal/:doc" element={<Legal />} />
          <Route path="/admin/*" element={<AdminApp />} />
          <Route
            path="/game"
            element={
              <RequireAuth>
                <GameLayout />
              </RequireAuth>
            }
          >
            <Route index element={<City />} />
            <Route path="fields" element={<City zone="field" />} />
            <Route path="map" element={<MapPage />} />
            <Route path="army" element={<Army />} />
            <Route path="heroes" element={<Heroes />} />
            <Route path="alliance" element={<Alliance />} />
            <Route path="politics" element={<Politics />} />
            <Route path="shop" element={<Shop />} />
            <Route path="shop/test-pay" element={<TestPay />} />
            <Route path="quests" element={<Quests />} />
            <Route path="reports" element={<Reports />} />
            <Route path="reports/:id" element={<Reports />} />
            <Route path="reports/sea/:seaId" element={<Reports />} />
            <Route path="marches" element={<Marches />} />
            {/* Чаты — в почте; старые ссылки ведут туда */}
            <Route path="chat" element={<Navigate to="/game/mail" replace />} />
            <Route path="mail" element={<Mail />} />
            <Route path="sea" element={<Sea />} />
            <Route path="sea/map" element={<Navigate to="/game/map" replace />} />
            <Route path="rankings" element={<Rankings />} />
            <Route path="battle-math" element={<BattleMath />} />
            <Route path="boosts" element={<Boosts />} />
            <Route path="battle-pass" element={<BattlePass />} />
            <Route path="events" element={<Events />} />
            <Route path="profile" element={<Profile />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </Suspense>
      <Toaster />
      <NewbieShieldPrompt />
      <CookieBanner />
      <AndroidUpdatePrompt />
      <BundleUpdateHint />
      <VerifyWatcher />
    </>
  );
}
